import { useState, useRef, useEffect } from "react"
import {
  TextField,
  Tooltip,
  CircularProgress,
  ClickAwayListener,
  Autocomplete,
} from "@mui/material"
import { createFilterOptions } from "@mui/material/Autocomplete"
import { IMaskInput } from "react-imask"
import { getContactEnterprises, createEnterprise } from "../../services/scout"
import { getStoredUser } from "../../services/auth"
import EditIcon from "@mui/icons-material/Edit"
import dayjs from "dayjs"

const dateSettings = { day: "2-digit", month: "2-digit", year: "numeric" }

const filter = createFilterOptions()

export default function EditableField({
  label,
  value,
  placeholder = "Clique para adicionar",
  onSave,
  type = "text",
  mask = null,
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [internalValue, setInternalValue] = useState(value || "") // stores id or value
  const [inputValue, setInputValue] = useState(label || value || "") // display text
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const wrapperRef = useRef(null)

  // Load enterprise options
  useEffect(() => {
    if (isEditing && type === "enterprise") {
      const fetchEnterprises = async () => {
        try {
          setLoadingOptions(true)
          const enterpriseId = getStoredUser().profile.enterprise?.id
          const data = await getContactEnterprises(enterpriseId)
          setOptions(data.results || data)
        } catch (err) {
          console.error("Failed to fetch enterprises", err)
        } finally {
          setLoadingOptions(false)
        }
      }
      fetchEnterprises()
    }
  }, [isEditing, type])

  // Reset values when editing mode closes
  useEffect(() => {
    if (!isEditing) {
      if (type === "date") {
        const formatted = value ? dayjs(value).format("YYYY-MM-DD") : ""
        setInternalValue(formatted)
        setInputValue(formatted)
      } else {
        setInternalValue(value || "")
        setInputValue(label || value || "")
      }
      setError(null)
    }
  }, [value, label, isEditing, type])

  const validate = (val) => {
    if (!val) return null
    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(val)) return "E-mail inválido"
    }
    return null
  }

  const handleSave = async () => {
    if (type !== "enterprise") {
      const trimmed = typeof internalValue === "string" ? internalValue.trim() : internalValue
      if (type === "date") {
        const current = value ? dayjs(value).format("YYYY-MM-DD") : ""
        if (trimmed === current) {
          setIsEditing(false)
          return
        }
      } else {
        const normalizedTrimmed = trimmed ?? ""
        const normalizedValue = value ?? ""
        if (normalizedTrimmed === normalizedValue) {
          setIsEditing(false)
          return
        }
      }
    }

    try {
      setIsSaving(true)
      setError(null)

      if (type !== "enterprise") {
        const trimmed = typeof internalValue === "string" ? internalValue.trim() : internalValue
        await onSave(trimmed)
      }

      setIsEditing(false)
    } catch (err) {
      console.error("EditableField save failed:", err)
      setError("Erro ao salvar")
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setInternalValue(value || "")
    setInputValue(label || value || "")
    setError(null)
    setIsEditing(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave()
    if (e.key === "Escape") handleCancel()
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (isEditing) handleSave()
      }}
    >
      <div
        ref={wrapperRef}
        className={`editable-field${isEditing ? " editing" : ""}`}
        onClick={(e) => {
          if (isEditing) {
            e.stopPropagation()
            return
          }
          setIsEditing(true)
        }}
      >
        <div className="editable-field__value">
          {isEditing ? (
            <Tooltip open={!!error} title={error || ""} placement="top" arrow>
              {type === "enterprise" ? (
                <Autocomplete
                  autoHighlight
                  freeSolo
                  filterOptions={(opts, params) => {
                    const filtered = filter(opts, params)

                    // If inputValue does not match any existing option, add "Criar nova empresa"
                    const isExisting = opts.some(
                      o => o.name.toLowerCase() === params.inputValue.toLowerCase()
                    )
                    if (params.inputValue !== "" && !isExisting) {
                      filtered.push({ id: "__create__", name: "Criar nova empresa" })
                    }

                    return filtered
                  }}
                  options={[
                    ...options,
                    ...(inputValue &&
                    !options.some(o => o.name.toLowerCase() === inputValue.toLowerCase())
                      ? [{ id: "__create__", name: "Criar nova empresa" }]
                      : []),
                  ]}
                  getOptionLabel={option => option.name || option}
                  value={
                    internalValue
                      ? options.find(o => o.id === internalValue) || { name: inputValue }
                      : null
                  }
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                  onChange={async (event, newValue) => {
                    if (!newValue) {
                      setInternalValue("")
                      setInputValue("")
                      await onSave(null, null)
                      setIsEditing(false)
                      return
                    }

                    // check if nothing changed
                    if (newValue && newValue.id === value?.id && newValue.name === label) {
                      setIsEditing(false)
                      return // nothing changed, skip save
                    }

                    if (newValue.id === "__create__") {
                      const enterpriseId = getStoredUser().profile.enterprise?.id
                      const created = await createEnterprise(enterpriseId, inputValue)
                      setInternalValue(created.id)
                      setInputValue(created.name)
                      await onSave(created.id, created.name)
                    } else if (typeof newValue === "string") {
                      // fallback if the user typed and pressed Enter without selecting
                      const enterpriseId = getStoredUser().profile.enterprise?.id
                      const created = await createEnterprise(enterpriseId, newValue)
                      setInternalValue(created.id)
                      setInputValue(created.name)
                      await onSave(created.id, created.name)
                    } else {
                      setInternalValue(newValue.id)
                      setInputValue(newValue.name)
                      await onSave(newValue.id, newValue.name)
                    }
                    setIsEditing(false)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      autoFocus
                      sx={{ width: 200, fontStyle: "normal" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: loadingOptions ? (
                          <CircularProgress size={16} />
                        ) : (
                          params.InputProps.endAdornment
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
              ) : mask ? (
                <TextField
                  autoFocus
                  size="small"
                  value={internalValue}
                  onChange={(e) => setInternalValue(e.target.value)}
                  sx={{ width: 200, fontStyle: "normal" }}
                  InputProps={{
                    inputComponent: IMaskInput,
                    inputProps: { mask: "000.000.000-00", unmask: false },
                  }}
                  fullWidth
                />
              ) : (
                <TextField
                  autoFocus
                  size="small"
                  type={type}
                  value={internalValue}
                  disabled={isSaving}
                  error={!!error}
                  onChange={(e) => {
                    const val = e.target.value
                    setInternalValue(val)
                    setError(validate(val.trim()))
                  }}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  sx={{ width: 200, fontStyle: "normal" }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Tooltip>
          ) : (
            <>
              <EditIcon className="editable-field__edit-icon" fontSize="small" />
              <span className={!inputValue ? "empty" : ""}>
                {type === "date"
                  ? value
                    ? new Date(value).toLocaleDateString(undefined, dateSettings)
                    : placeholder
                  : inputValue || placeholder}
              </span>
            </>
          )}
        </div>
      </div>
    </ClickAwayListener>
  )
}